import { useMemo, useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import intl from 'react-intl-universal';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Icon } from '@getsynapse/design-system';
import { selectUser } from 'state/User/userSlice';
import morningIcon from 'assets/icons/morning-icon.svg';
import afternoonIcon from 'assets/icons/afternoon-icon.svg';
import eveningIcon from 'assets/icons/evening-icon.svg';
import PageTitle from 'Molecules/PageTitle/PageTitle';
import MentionsCard from './components/RecentMentions/MentionsCard';
import ProjectsCard from './components/DueProjects/ProjectsCard';
import RequestsCard from './components/InReviewRequests/RequestsCard';
import TasksCard from './components/DueTasks/TasksCard';
import V2TasksCard from './components/V2DueTasks/TasksCard';
import {
  getDueProjects,
  getDueTasks,
  getRecentMentions,
  getRequestsInReview,
  setTablesLimit,
  setMentionsLimit,
} from 'state/MyWorkspace/myWorkspaceSlice';
import {
  LARGE_SCREEN_WIDTH,
  NUMBER_OF_ROWS_IN_SMALL_SCREENS,
  NUMBER_OF_ROWS_IN_LARGE_SCREENS,
  NUMBER_OF_MENTIONS_IN_SMALL_SCREENS,
  NUMBER_OF_MENTIONS_IN_LARGE_SCREENS,
  USER_TYPES,
} from 'utils/constants';
import ActionsNotificationsList from 'Pages/TaskDetailPage/ActualHoursModal/ActionsNotificationsList/ActionsNotificationsList';
import CloseProject from 'Pages/ProjectPage/components/CloseProject/CloseProject';
import CancelProject from 'Pages/ProjectPage/components/CancelProject/CancelProject';
import PutProjectOnHoldModal from 'Pages/ProjectPage/components/PutProjectOnHoldModal/PutProjectOnHoldModal';
import { bulkUpdateProjects } from 'api/projects';
import { showNotification as showSnackbarNotification } from 'state/SnackbarNotification/SnackbarNotificationSlice';
import {
  getOriginalProjectData,
  fetchProject,
  resetProject,
} from 'state/Project/projectSlice';
import { useAppThunkDispatch } from 'state/store';

const MyWorkspace = () => {
  const { newTaskListViews = false } = useFlags();
  const currentHour = new Date().getHours();
  const userName = useSelector(selectUser).firstName;
  const userType = useSelector(selectUser).type;
  const dispatch = useAppThunkDispatch();
  const selectedProject = useSelector(getOriginalProjectData);

  const [displayCancelModal, setDisplayCancelModal] = useState(false);
  const [displayOnHoldModal, setDisplayOnHoldModal] = useState(false);
  const [displayCloseModal, setDisplayCloseModal] = useState(false);

  useEffect(() => {
    if (!newTaskListViews) {
      dispatch(getDueTasks());
    }
    userType === USER_TYPES.L_D && dispatch(getRequestsInReview());
    dispatch(getRecentMentions());
    // move to new get projects endpoint to include permissions
    dispatch(getDueProjects());
    dispatch(
      setTablesLimit(
        window.innerWidth <= LARGE_SCREEN_WIDTH
          ? NUMBER_OF_ROWS_IN_SMALL_SCREENS
          : NUMBER_OF_ROWS_IN_LARGE_SCREENS
      )
    );
    dispatch(
      setMentionsLimit(
        window.innerWidth <= LARGE_SCREEN_WIDTH
          ? NUMBER_OF_MENTIONS_IN_SMALL_SCREENS
          : NUMBER_OF_MENTIONS_IN_LARGE_SCREENS
      )
    );
  }, [dispatch, userType, newTaskListViews]);

  const handleOpenCancelModal = useCallback(
    (projectId: string) => {
      dispatch(resetProject());
      dispatch(fetchProject(projectId));
      setDisplayCancelModal(true);
    },
    [dispatch]
  );

  const handleOpenOnHoldModal = useCallback(
    (projectId: string) => {
      dispatch(resetProject());
      dispatch(fetchProject(projectId));
      setDisplayOnHoldModal(true);
    },
    [dispatch]
  );

  const handleOpenCloseModal = useCallback(
    (projectId: string) => {
      dispatch(resetProject());
      dispatch(fetchProject(projectId));
      setDisplayCloseModal(true);
    },
    [dispatch]
  );

  const handleAfterStatusChange = useCallback(() => {
    dispatch(getDueProjects());
  }, [dispatch]);

  const handleCloseCancelModal = useCallback(() => {
    setDisplayCancelModal(false);
    dispatch(resetProject());
  }, [dispatch]);

  const handleCloseOnHoldModal = useCallback(() => {
    setDisplayOnHoldModal(false);
    dispatch(resetProject());
  }, [dispatch]);

  const handleCloseCloseModal = useCallback(() => {
    setDisplayCloseModal(false);
    dispatch(resetProject());
  }, [dispatch]);

  const handleConfirmCancelProject = useCallback(
    async (reason: string, archive: boolean) => {
      if (selectedProject?.id) {
        const response = await bulkUpdateProjects({
          ids: [selectedProject.id],
          status: 'canceled',
          cancelReason: reason,
          cancellationDate: new Date().toISOString(),
          isArchived: archive,
        });

        const data = Array.isArray(response.data)
          ? response.data[0]
          : response.data;
        const { successfulCount, failedCount } = data.attributes;

        if (successfulCount > 0) {
          setDisplayCancelModal(false);
          handleAfterStatusChange();
        } else if (failedCount > 0) {
          dispatch(
            showSnackbarNotification({
              notificationVariant: 'error',
              notificationTitle: intl.get(
                'PROJECT_DETAIL.PROJECT_STATUS_UPDATE_ERROR.TITLE'
              ),
              notificationMessage: intl.get(
                'PROJECT_DETAIL.PROJECT_STATUS_UPDATE_ERROR.MESSAGE'
              ),
            })
          );
        }
      }
    },
    [dispatch, selectedProject, handleAfterStatusChange]
  );

  const handleConfirmOnHoldProject = useCallback(
    async (holdReason: string) => {
      if (selectedProject?.id) {
        const response = await bulkUpdateProjects({
          ids: [selectedProject.id],
          status: 'on_hold',
          holdReason,
        });

        const data = Array.isArray(response.data)
          ? response.data[0]
          : response.data;
        const { successfulCount, failedCount } = data.attributes;

        if (successfulCount > 0) {
          setDisplayOnHoldModal(false);
          handleAfterStatusChange();
        } else if (failedCount > 0) {
          dispatch(
            showSnackbarNotification({
              notificationVariant: 'error',
              notificationTitle: intl.get(
                'PROJECT_DETAIL.PROJECT_STATUS_UPDATE_ERROR.TITLE'
              ),
              notificationMessage: intl.get(
                'PROJECT_DETAIL.PROJECT_STATUS_UPDATE_ERROR.MESSAGE'
              ),
            })
          );
        }
      }
    },
    [dispatch, selectedProject, handleAfterStatusChange]
  );

  const handleConfirmCloseProject = useCallback(
    async (comments: string, closingDate: string, archive: boolean) => {
      if (selectedProject?.id) {
        const response = await bulkUpdateProjects({
          ids: [selectedProject.id],
          status: 'closed',
          closedDate: closingDate,
          finalComments: comments,
          isArchived: archive,
        });

        const data = Array.isArray(response.data)
          ? response.data[0]
          : response.data;
        const { successfulCount, failedCount } = data.attributes;

        if (successfulCount > 0) {
          setDisplayCloseModal(false);
          handleAfterStatusChange();
        } else if (failedCount > 0) {
          dispatch(
            showSnackbarNotification({
              notificationVariant: 'error',
              notificationTitle: intl.get(
                'PROJECT_DETAIL.PROJECT_STATUS_UPDATE_ERROR.TITLE'
              ),
              notificationMessage: intl.get(
                'PROJECT_DETAIL.PROJECT_STATUS_UPDATE_ERROR.MESSAGE'
              ),
            })
          );
        }
      }
    },
    [dispatch, selectedProject, handleAfterStatusChange]
  );

  const greetingMessage = useMemo(
    () => (
      <div className='flex items-center'>
        {currentHour >= 0 && currentHour <= 11 ? (
          <>
            {intl.get('MY_WORKSPACE.MORNING_GREETING', { userName })}
            <Icon src={morningIcon} className='ml-1' />
          </>
        ) : currentHour >= 12 && currentHour <= 17 ? (
          <>
            {intl.get('MY_WORKSPACE.AFTERNOON_GREETING', { userName })}
            <Icon src={afternoonIcon} className='ml-1' />
          </>
        ) : (
          <>
            {intl.get('MY_WORKSPACE.EVENING_GREETING', { userName })}
            <Icon src={eveningIcon} className='ml-1' />
          </>
        )}
      </div>
    ),
    [currentHour, userName]
  );

  return (
    <>
      <PageTitle titleComponent={greetingMessage} />
      <ActionsNotificationsList />

      <div
        data-testid='sammy'
        className='grid grid-cols-2 gap-4 pt-3 px-6 pb-11'
      >
        {newTaskListViews ? <V2TasksCard /> : <TasksCard />}
        <ProjectsCard
          onOpenCancelModal={handleOpenCancelModal}
          onOpenOnHoldModal={handleOpenOnHoldModal}
          onOpenCloseModal={handleOpenCloseModal}
        />
        {userType === USER_TYPES.L_D ? <RequestsCard /> : null}
        <MentionsCard />
      </div>

      {selectedProject && (
        <>
          <CancelProject
            shouldDisplayModal={displayCancelModal}
            closeModalCallback={handleCloseCancelModal}
            confirmCancelProject={handleConfirmCancelProject}
            cancelReason={selectedProject.cancelReason}
            cancellationDate={selectedProject.cancellationDate}
          />

          <PutProjectOnHoldModal
            shouldDisplayModal={displayOnHoldModal}
            onClose={handleCloseOnHoldModal}
            onCancelCallback={handleCloseOnHoldModal}
            onConfirm={handleConfirmOnHoldProject}
            holdReason={selectedProject.holdReason}
          />

          <CloseProject
            shouldDisplayModal={displayCloseModal}
            closeModalCallback={handleCloseCloseModal}
            confirmCloseProject={handleConfirmCloseProject}
            project={selectedProject}
          />
        </>
      )}
    </>
  );
};

export default MyWorkspace;
